import {
  InvestorType,
  DealVolume,
  TeamSize,
} from '../components/Trial/PlanQuestionnaire'

export interface RecommendationResult {
  plan: 'Core' | 'Pro' | 'Scale'
  confidence: number
  reasoning: string[]
  relevantFeatures: string[]
}

interface QuestionnaireAnswers {
  dealVolume: DealVolume
  investorType: InvestorType
  teamSize: TeamSize
}

export function recommendPlan(
  answers: QuestionnaireAnswers,
): RecommendationResult {
  const scores = {
    Core: 0,
    Pro: 0,
    Scale: 0,
  }

  // Score based on deal volume (highest weight)
  const dealVolumeNum = parseDealVolume(answers.dealVolume)
  if (dealVolumeNum <= 2) {
    scores.Core += 5
    scores.Pro += 2
  } else if (dealVolumeNum <= 8) {
    scores.Core += 1
    scores.Pro += 5
    scores.Scale += 2
  } else if (dealVolumeNum <= 20) {
    scores.Pro += 2
    scores.Scale += 5
  } else {
    scores.Scale += 6
  }

  // Score based on investor type
  switch (answers.investorType) {
    case 'new':
      scores.Core += 3
      scores.Pro += 1
      break
    case 'buy-hold':
      scores.Core += 2
      scores.Pro += 2
      scores.Scale += 1
      break
    case 'fix-flip':
      scores.Core += 1
      scores.Pro += 3
      scores.Scale += 2
      break
    case 'wholesaler':
      scores.Pro += 2
      scores.Scale += 4
      break
    case 'str':
      scores.Core += 1
      scores.Pro += 2
      scores.Scale += 2
      break
  }

  // Score based on team size
  const teamSizeNum = parseTeamSize(answers.teamSize)
  if (teamSizeNum === 1) {
    scores.Core += 2
    scores.Pro += 1
  } else if (teamSizeNum <= 5) {
    scores.Pro += 2
    scores.Scale += 1
  } else if (teamSizeNum <= 15) {
    scores.Pro += 1
    scores.Scale += 3
  } else {
    scores.Scale += 4
  }

  // Determine recommended plan
  const maxScore = Math.max(scores.Core, scores.Pro, scores.Scale)
  let recommendedPlan: 'Core' | 'Pro' | 'Scale' = 'Core'
  if (maxScore === scores.Scale) recommendedPlan = 'Scale'
  else if (maxScore === scores.Pro) recommendedPlan = 'Pro'

  const totalScore = scores.Core + scores.Pro + scores.Scale
  const confidence = Math.round((maxScore / totalScore) * 100)

  // Generate reasoning
  const reasoning = generateReasoning(answers, recommendedPlan)

  // Get relevant features
  const relevantFeatures = getRelevantFeatures(
    answers.investorType,
    recommendedPlan,
  )

  return {
    plan: recommendedPlan,
    confidence,
    reasoning,
    relevantFeatures,
  }
}

function parseDealVolume(volume: DealVolume): number {
  const match = volume.match(/\d+/)
  return match ? parseInt(match[0]) : 0
}

function parseTeamSize(size: TeamSize): number {
  if (size === '1') return 1
  if (size === '16+') return 16
  const match = size.match(/\d+/)
  return match ? parseInt(match[0]) : 1
}

function generateReasoning(
  answers: QuestionnaireAnswers,
  plan: 'Core' | 'Pro' | 'Scale',
): string[] {
  const reasons: string[] = []

  const dealVolumeNum = parseDealVolume(answers.dealVolume)

  // Deal volume reasoning
  if (plan === 'Core') {
    reasons.push(
      `1,500 offers/month is ideal for investors closing ${answers.dealVolume} deals per month`,
    )
  } else if (plan === 'Pro') {
    if (dealVolumeNum <= 8) {
      reasons.push(
        `4,500 offers/month gives you the volume to consistently close ${answers.dealVolume} deals`,
      )
    } else {
      reasons.push(`4,500 offers/month with room to scale your deal flow`)
    }
  } else {
    if (dealVolumeNum >= 9) {
      reasons.push(
        `Unlimited offers support your ${answers.dealVolume} deals/month volume`,
      )
    } else {
      reasons.push(`Unlimited offers and markets for maximum deal flow`)
    }
  }

  // Investor type reasoning
  const investorTypeReasons: Record<
    InvestorType,
    { Core: string; Pro: string; Scale: string }
  > = {
    wholesaler: {
      Core: 'Automated offers to get your wholesaling pipeline started',
      Pro: 'High-volume offer automation built for active wholesalers',
      Scale: 'Unlimited offers perfect for high-volume wholesaling operations',
    },
    'fix-flip': {
      Core: 'AI analysis helps evaluate renovation opportunities',
      Pro: 'Smart follow-up and multi-market campaigns for flippers',
      Scale: 'Custom AI training for your specific flip criteria',
    },
    'buy-hold': {
      Core: 'Automated offers to build your rental portfolio',
      Pro: 'Multi-market campaigns for portfolio expansion',
      Scale: 'Unlimited markets and custom AI for portfolio growth',
    },
    str: {
      Core: 'Property analysis tools for STR evaluation',
      Pro: 'Multi-market campaigns for STR portfolio growth',
      Scale: 'Unlimited markets and white-labeled offers for STR scaling',
    },
    new: {
      Core: 'Affordable plan to learn and start closing deals',
      Pro: 'More volume to accelerate your learning and deal flow',
      Scale: 'All features plus dedicated support to fast-track your growth',
    },
  }

  reasons.push(investorTypeReasons[answers.investorType][plan])

  // Team size reasoning
  const teamSizeNum = parseTeamSize(answers.teamSize)
  if (teamSizeNum === 1) {
    reasons.push('Designed for solo investors with focused features')
  } else if (teamSizeNum <= 5) {
    if (plan === 'Core') reasons.push('Supports small team collaboration')
    else if (plan === 'Pro')
      reasons.push('Great capacity for small team operations')
    else reasons.push('Advanced features for team coordination')
  } else {
    reasons.push('Enterprise-grade features for larger teams')
  }

  return reasons
}

function getRelevantFeatures(
  investorType: InvestorType,
  plan: 'Core' | 'Pro' | 'Scale',
): string[] {
  const featuresByType: Record<
    InvestorType,
    { Core: string[]; Pro: string[]; Scale: string[] }
  > = {
    wholesaler: {
      Core: [
        '1,500 automated offers/month',
        'Response tracking',
        'Deal pipeline analytics',
      ],
      Pro: [
        '4,500 automated offers/month',
        'Smart response tracking & follow-up',
        'Multi-market campaigns',
      ],
      Scale: [
        'Unlimited automated offers',
        'White-labeled offer system',
        'Dedicated account manager',
      ],
    },
    'fix-flip': {
      Core: [
        'AI-powered property analysis',
        'Deal pipeline analytics',
        'Custom offer templates',
      ],
      Pro: [
        '4,500 automated offers/month',
        'AI-powered property analysis',
        'Multi-market campaigns',
      ],
      Scale: [
        'Custom AI training',
        'API & CRM integrations',
        'Custom onboarding & setup',
      ],
    },
    'buy-hold': {
      Core: [
        'AI-powered property analysis',
        'Response tracking',
        'Deal pipeline analytics',
      ],
      Pro: [
        '4,500 automated offers/month',
        'Multi-market campaigns',
        'Smart response tracking & follow-up',
      ],
      Scale: [
        'Unlimited markets & offers',
        'Custom AI training',
        'Dedicated account manager',
      ],
    },
    str: {
      Core: [
        'AI-powered property analysis',
        'Unlimited markets',
        'Custom offer templates',
      ],
      Pro: [
        '4,500 automated offers/month',
        'Multi-market campaigns',
        'Smart response tracking & follow-up',
      ],
      Scale: [
        'Unlimited offers',
        'Custom AI training',
        'White-labeled offer system',
      ],
    },
    new: {
      Core: [
        'AI-powered property analysis',
        'Email support',
        'Deal pipeline analytics',
      ],
      Pro: [
        '4,500 automated offers/month',
        'Email & phone support',
        'Multi-market campaigns',
      ],
      Scale: [
        'Dedicated account manager',
        '24/7 priority support',
        'Custom onboarding & setup',
      ],
    },
  }

  return featuresByType[investorType][plan]
}
